import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Message } from 'primereact/message';
import { ProgressSpinner } from 'primereact/progressspinner';
import type { Perfil, Responsable } from '../../negocio/negocio.types';
import type { User } from '../../users/types/users.types';
import { PerfilesService } from '../services/perfiles.service';
import { UsersService } from '../../users/services/users.service';
import { ResponsablesService } from '../../responsables/services/responsables.service';
import { ETIQUETA_MODULO, MODULOS_SENSIBLES, ordenarModulos } from '../utils/perfiles';
import { admitePerfil, textoAlcances } from '../../users/utils/sesiones';
import { mensajeDeError, noDisponible } from '../../negocio/errores';
import { PerfilDialog } from '../components/PerfilDialog';
import { AsignarPerfilForm } from '../components/AsignarPerfilForm';

const porNombre = (a: { nombre: string }, b: { nombre: string }) => a.nombre.localeCompare(b.nombre, 'es');

/**
 * Perfiles de Consulta CM (fase 3). Sólo superadmin y admin.
 *
 * Un perfil es una lista de módulos; sólo QUITA. Arriba se crean y editan;
 * abajo se asigna perfil y alcance de Consulta CM a cada cuenta que lo admite
 * (superadmin, admin e indicadores). Las cuentas de tienda, bodega, vendedor y
 * consulta se quedan en Venta Asistida.
 */
const PerfilesScreen: React.FC = () => {
  const [lista, setLista] = useState<Perfil[] | null>(null);
  const [cuentas, setCuentas] = useState<User[]>([]);
  const [responsables, setResponsables] = useState<Responsable[]>([]);
  const [sinRuta, setSinRuta] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [editando, setEditando] = useState<Perfil | null | 'nuevo'>(null);
  const [asignando, setAsignando] = useState<User | null>(null);
  const [texto, setTexto] = useState('');

  const cargarPerfiles = useCallback(async () => {
    try {
      setLista([...(await PerfilesService.listar())].sort(porNombre));
    } catch (e) {
      if (noDisponible(e)) setSinRuta(true);
      else setError(mensajeDeError(e));
    }
  }, []);

  const cargarCuentas = useCallback(async () => {
    try {
      setCuentas(await UsersService.getUsers());
    } catch (e) {
      setError(mensajeDeError(e));
    }
  }, []);

  useEffect(() => {
    void cargarPerfiles();
    void cargarCuentas();
    // Los responsables sólo dan las opciones de alcance: si fallan, se asigna sin ellas.
    ResponsablesService.listar()
      .then(setResponsables)
      .catch(() => setResponsables([]));
  }, [cargarPerfiles, cargarCuentas]);

  const elegibles = useMemo(() => {
    const q = texto.trim().toLowerCase();
    return cuentas
      .filter((c) => admitePerfil(c.role) && !c.deletedAt)
      .filter((c) => !q || `${c.email} ${c.name ?? ''} ${c.perfil?.nombre ?? ''}`.toLowerCase().includes(q))
      .sort((a, b) => a.email.localeCompare(b.email));
  }, [cuentas, texto]);

  const borrar = async (p: Perfil) => {
    if (!window.confirm(`¿Borrar el perfil «${p.nombre}»? No se puede deshacer.`)) return;
    setError(null);
    try {
      await PerfilesService.borrar(p.id);
      setAviso(`Perfil «${p.nombre}» borrado.`);
      await cargarPerfiles();
    } catch (e) {
      setError(mensajeDeError(e));
    }
  };

  if (sinRuta) {
    return (
      <Message
        severity="info"
        className="w-full justify-start"
        text="La API de este entorno todavía no ofrece Perfiles (llegan con Consulta CM). En el admin todo sigue como hoy."
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Perfiles</h1>
          <p className="mt-1 max-w-3xl text-sm text-gray-600">
            Qué módulos ve cada cuenta en Consulta CM (y en Metas del admin). Un perfil sólo quita: una cuenta sin perfil
            sigue en el admin como hoy, pero no entra a Consulta CM.
          </p>
        </div>
        <Button label="Crear perfil" icon="pi pi-plus" onClick={() => setEditando('nuevo')} />
      </div>

      {error && <Message severity="error" text={error} className="w-full justify-start" />}
      {aviso && <Message severity="success" text={aviso} className="w-full justify-start" />}

      {!lista && !error && (
        <div className="flex justify-center p-6">
          <ProgressSpinner style={{ width: '2.5rem', height: '2.5rem' }} />
        </div>
      )}

      {lista && (
        <div className="overflow-x-auto rounded-md border border-solid border-gray-200 bg-white">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-3 py-2">Perfil</th>
                <th className="px-3 py-2">Módulos</th>
                <th className="px-3 py-2">Cuentas</th>
                <th className="px-3 py-2">Estado</th>
                <th className="px-3 py-2 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {lista.map((p) => {
                const noBorra = p.deFabrica
                  ? 'Perfil de fábrica: se edita o se desactiva, no se borra'
                  : p.usuarios > 0
                    ? 'Tiene cuentas asignadas'
                    : null;
                return (
                  <tr key={p.id} data-testid={`perfil-${p.nombre}`} className="border-t border-solid border-gray-100 align-top">
                    <td className="px-3 py-2">
                      <b>{p.nombre}</b>
                      <div className="text-xs text-gray-500">
                        {p.clave}
                        {p.deFabrica ? ' · de fábrica' : ''}
                      </div>
                      {p.descripcion && <div className="mt-1 text-xs text-gray-500">{p.descripcion}</div>}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap gap-1">
                        {ordenarModulos(p.modulos).map((m) => (
                          <span
                            key={m}
                            className={`rounded-full px-2 py-0.5 text-xs ${MODULOS_SENSIBLES.includes(m) ? 'bg-amber-100 text-amber-900' : 'bg-blue-50 text-blue-800'}`}
                          >
                            {ETIQUETA_MODULO[m]}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-3 py-2">{p.usuarios}</td>
                    <td className="px-3 py-2">
                      {p.activo ? (
                        <span className="rounded-full bg-green-50 px-2 py-0.5 text-xs text-green-800">Activo</span>
                      ) : (
                        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">Desactivado</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex justify-end gap-1">
                        <Button
                          icon="pi pi-pencil"
                          className="p-button-text p-button-sm"
                          aria-label={`Editar ${p.nombre}`}
                          tooltip="Editar"
                          onClick={() => setEditando(p)}
                        />
                        <Button
                          icon="pi pi-trash"
                          className="p-button-text p-button-sm p-button-danger"
                          aria-label={`Borrar ${p.nombre}`}
                          tooltip={noBorra ?? 'Borrar'}
                          tooltipOptions={{ showOnDisabled: true }}
                          disabled={!!noBorra}
                          onClick={() => void borrar(p)}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <section className="space-y-3">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Cuentas</h2>
            <p className="text-sm text-gray-600">
              Superadmin, admin e indicadores. El alcance de Metas se asigna en Metas › Accesos.
            </p>
          </div>
          <input
            aria-label="Buscar cuenta"
            className="w-full rounded-md border border-solid border-gray-300 bg-white px-3 py-2 text-sm sm:w-64"
            placeholder="Buscar cuenta o perfil…"
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
          />
        </div>
        <div className="overflow-x-auto rounded-md border border-solid border-gray-200 bg-white" data-testid="cuentas-perfil">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-3 py-2">Cuenta</th>
                <th className="px-3 py-2">Rol</th>
                <th className="px-3 py-2">Perfil</th>
                <th className="px-3 py-2">Alcance en Consulta CM</th>
                <th className="px-3 py-2 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {elegibles.map((c) => (
                <tr key={c.id} className="border-t border-solid border-gray-100">
                  <td className="px-3 py-2">
                    <div>{c.email}</div>
                    {c.name && <div className="text-xs text-gray-500">{c.name}</div>}
                  </td>
                  <td className="px-3 py-2">{c.roleDisplay || c.role}</td>
                  <td className="px-3 py-2">{c.perfil ? c.perfil.nombre : <span className="text-gray-500">Sin perfil</span>}</td>
                  <td className="px-3 py-2">{textoAlcances([], c.alcancesNegocio ?? [])}</td>
                  <td className="px-3 py-2">
                    <div className="flex justify-end">
                      <Button
                        icon="pi pi-id-card"
                        className="p-button-text p-button-sm"
                        aria-label={`Asignar perfil a ${c.email}`}
                        tooltip="Asignar perfil y alcance"
                        onClick={() => setAsignando(c)}
                      />
                    </div>
                  </td>
                </tr>
              ))}
              {elegibles.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-3 py-6 text-center text-gray-500">
                    No hay cuentas que coincidan.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {editando && (
        <PerfilDialog
          perfil={editando === 'nuevo' ? null : editando}
          onHide={() => setEditando(null)}
          onGuardado={(p) => {
            setEditando(null);
            setAviso(`Perfil «${p.nombre}» guardado.`);
            void cargarPerfiles();
          }}
        />
      )}
      {asignando && (
        <Dialog
          header={`Perfil y alcance · ${asignando.email}`}
          visible
          onHide={() => setAsignando(null)}
          style={{ width: '36rem' }}
          breakpoints={{ '640px': '95vw' }}
        >
          <AsignarPerfilForm
            usuario={asignando}
            perfiles={lista ?? []}
            responsables={responsables}
            onGuardado={(r) => {
              // Se queda abierto para que se lean los avisos; la lista y los conteos se recargan.
              setCuentas((xs) =>
                xs.map((x) => (x.id === r.userId ? { ...x, perfil: r.perfil, alcancesNegocio: r.alcancesNegocio } : x)),
              );
              void cargarPerfiles();
            }}
          />
        </Dialog>
      )}
    </div>
  );
};

export default PerfilesScreen;
